/**
 * components/support/help-copies.ts
 *
 * The colleagues an answer from Optivo is copied to, as the "Ask Optivo"
 * window takes them: typed, pasted as a list, copied out of a mail client
 * with their owners' names beside them, or picked from the colleagues the
 * account already has.
 *
 * The window checks an address before it is sent, so the operator hears why
 * while it is still in the box; the server checks it again
 * (`optivo-service/src/services/core/emailCopies.ts`), and
 * `help-copies.parity.test.ts` holds the two rules together.
 */

/** Copies one answer carries: the people who work on the property, not a mailing list. */
export const MAX_HELP_COPIES = 5;

/** The longest address a mailbox can have. */
export const MAX_HELP_COPY_ADDRESS_CHARS = 254;

/**
 * One mailbox, written the way a header carries it bare: nothing that would
 * separate addresses or open a display name.
 */
export const HELP_COPY_ADDRESS = /^[^\s@,;<>"()[\]\\]+@[^\s@,;<>"()[\]\\]+\.[^\s@,;<>"()[\]\\]+$/;

export function isHelpCopyAddress(value: string): boolean {
    return value.length <= MAX_HELP_COPY_ADDRESS_CHARS && HELP_COPY_ADDRESS.test(value);
}

/** Why something typed did not become a copy. */
export type HelpCopyProblem =
    | { code: "invalid"; address: string }
    | { code: "own" }
    | { code: "tooMany" };

export interface HelpCopiesResult {
    /** The copies, as they stand now. */
    copies: string[];
    /** What stays in the box: what is not an address, and what did not fit. */
    rest: string;
    /** The first thing worth saying about what was typed; null when all of it went in. */
    problem: HelpCopyProblem | null;
}

/**
 * What a box holds, as the addresses in it: a mail client's `Name <address>`
 * read as the address alone, then split at every comma, semicolon or space —
 * none of which an address can hold.
 */
function addressesIn(text: string): string[] {
    return text
        .replace(/[^,;<>]*<([^<>]*)>/g, " $1 ")
        .split(/[\s,;]+/)
        .map((part) => part.trim())
        .filter((part) => part.length > 0);
}

/**
 * Add what was typed to the copies: each address once, in lower case. The
 * operator's own address is left out — the answer comes to them anyway — and
 * said so; what is not an address stays in the box to be corrected, named;
 * and past {@link MAX_HELP_COPIES} what does not fit stays in the box too.
 */
export function addHelpCopies(current: readonly string[], text: string, ownEmail: string | null): HelpCopiesResult {
    const own = ownEmail?.trim().toLowerCase() ?? null;
    const copies = [...current];
    const rest: string[] = [];
    let problem: HelpCopyProblem | null = null;
    const note = (next: HelpCopyProblem) => {
        problem ??= next;
    };

    for (const typed of addressesIn(text)) {
        const address = typed.toLowerCase();
        if (!isHelpCopyAddress(address)) {
            rest.push(typed);
            note({ code: "invalid", address: typed });
        } else if (address === own) {
            note({ code: "own" });
        } else if (copies.includes(address)) {
            continue;
        } else if (copies.length >= MAX_HELP_COPIES) {
            rest.push(typed);
            note({ code: "tooMany" });
        } else {
            copies.push(address);
        }
    }
    return { copies, rest: rest.join(", "), problem };
}
